import { FC, useMemo } from 'react'
import cn from 'classnames'
import type { Field } from '@/api/types/crm/crm.types'
import LeadField from '../../components/LeadField/LeadField'
import LeadProfileModalBlock from '../../components/LeadProfileModalBlock/LeadProfileModalBlock'
import useFullLeadModalAccess from '../../hooks/useFullLeadModalAccess'
import {
  filterUtmLeadFields,
  getUtmLeadFieldTooltip,
} from '@pages/Admin/CRM/utils/utmLeadFields'
import {
  buildDealUtmLeadFields,
  dealUtmKeyFromSyntheticUniqueName,
  getDealUtmFieldTooltip,
  type DealUtm,
  type DealUtmKey,
} from '../../LeadProfileModalContent/dealUtm.utils'
import aboutClasses from '../About/index.module.css'
import compactFieldClasses from '@pages/Admin/CRM/styles/compactLeadFields.module.css'

interface UtmTagsContentProps {
  fields: Field[]
  fieldsData: Record<string, string>
  updateFieldData: (uniqueNameField: string, value: string) => void
  /** Режим сделок: UTM со сделки, не из анкеты лида. */
  multipleDealsEnabled?: boolean
  dealUtmDraft?: DealUtm
  onChangeDealUtmField?: (key: DealUtmKey, value: string) => void
}

const UtmTagsContent: FC<UtmTagsContentProps> = ({
  fields,
  fieldsData,
  updateFieldData,
  multipleDealsEnabled = false,
  dealUtmDraft,
  onChangeDealUtmField,
}) => {
  const access = useFullLeadModalAccess()
  const utmFields = filterUtmLeadFields(fields)
  const dealUtmFields = useMemo(() => buildDealUtmLeadFields(), [])

  if (multipleDealsEnabled) {
    return (
      <LeadProfileModalBlock
        aria-label="UTM метки"
        title="UTM метки"
        titleHint={multipleDealsEnabled ? 'по сделке' : undefined}
      >
        <div
          className={cn(aboutClasses.listSmallFields, compactFieldClasses.root)}
        >
          {dealUtmFields.map((field) => {
            const key = dealUtmKeyFromSyntheticUniqueName(field.unique_name)
            if (!key) return null
            const value = dealUtmDraft?.[key] ?? ''

            return (
              <LeadField
                key={field.id}
                field={field}
                size="small"
                value={value == null ? '' : String(value)}
                onChange={(val) => onChangeDealUtmField?.(key, val)}
                tooltipTitle={getDealUtmFieldTooltip(key)}
                disabled={!access.canEditFields}
              />
            )
          })}
        </div>
      </LeadProfileModalBlock>
    )
  }

  return (
    <LeadProfileModalBlock aria-label="UTM метки" title="UTM метки">
      {utmFields.length === 0 ? (
        <p className={aboutClasses.aboutEmpty}>
          UTM-поля не настроены для этой воронки.
        </p>
      ) : (
        <div
          className={cn(aboutClasses.listSmallFields, compactFieldClasses.root)}
        >
          {utmFields.map((field) => (
            <LeadField
              key={field.id}
              field={field}
              size="small"
              value={fieldsData[field.unique_name] ?? ''}
              onChange={(val) => updateFieldData(field.unique_name, val)}
              tooltipTitle={
                getUtmLeadFieldTooltip(field.unique_name) ?? field.name
              }
              disabled={!access.canEditFields}
            />
          ))}
        </div>
      )}
    </LeadProfileModalBlock>
  )
}

export default UtmTagsContent
